import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "../components/admin/AdminLayout";
import PrimaryActionButton from "../components/admin/PrimaryActionButton";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { EDITIONS, editionSourceLabel } from "../config/adminConfigs";

/** Reader-facing edition name ("Janmabhoomi", not the "Janambhoomi" key). */
const editionLabel = (key) => editionSourceLabel(key) || key;

// Mirrors the server's slug rule (server/src/models/Category.js).
const SLUG_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;
const LABEL_FIELDS = [
  { lang: "en", label: "English" },
  { lang: "gu", label: "Gujarati" },
  { lang: "hi", label: "Hindi" },
  { lang: "mr", label: "Marathi" }
];
const EMPTY_LABELS = { en: "", gu: "", hi: "", mr: "" };
const EMPTY_FORM = { slug: "", name: "", labels: EMPTY_LABELS, is_active: true };

/** Suggests a slug from an English name ("Agri News" -> "agri_news"). */
const suggestSlug = (name) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[^a-z]+/, "")
    .replace(/_+$/, "")
    .slice(0, 40);

const errorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.errors?.length) return data.errors.map((e) => e.msg).join(". ");
  return data?.message || fallback;
};

const CategoryForm = ({ initial, isNew, saving, serverError, editionName, onCancel, onSubmit }) => {
  const [form, setForm] = useState(initial);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [formError, setFormError] = useState("");

  const setName = (name) =>
    setForm((prev) => ({ ...prev, name, slug: slugTouched ? prev.slug : suggestSlug(name) }));

  const submit = (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const slug = form.slug.trim().toLowerCase();
    if (!name) return setFormError("Name is required.");
    if (isNew && !SLUG_PATTERN.test(slug)) {
      return setFormError(
        "Slug must be 2-40 characters: lowercase letters, digits or underscores, starting with a letter."
      );
    }
    setFormError("");
    return onSubmit({ ...form, name, slug });
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <form className="modal-card" onSubmit={submit}>
        <h2>
          {isNew ? `Add category to ${editionName}` : `Edit "${initial.name}" (${editionName})`}
        </h2>
        {(formError || serverError) && <p className="error-banner">{formError || serverError}</p>}

        <label htmlFor="cat-name">Name *</label>
        <input
          id="cat-name"
          value={form.name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Agriculture"
          required
        />

        <label htmlFor="cat-slug">Slug {isNew ? "*" : ""}</label>
        <input
          id="cat-slug"
          value={form.slug}
          maxLength={40}
          disabled={!isNew}
          onChange={(e) => {
            setSlugTouched(true);
            setForm((prev) => ({ ...prev, slug: e.target.value.toLowerCase() }));
          }}
          placeholder="e.g. agriculture"
          required={isNew}
        />
        <p className="category-hint">
          {isNew
            ? "Stored on every article in this category. It can't be changed later."
            : "The slug can't be changed: articles are filed under it."}
        </p>

        <fieldset className="category-labels">
          <legend>App labels (optional)</legend>
          <p className="category-hint">
            Shown in the app for that language. Leave empty to keep the app&apos;s own label for
            built-in sections, or the name above for new ones.
          </p>
          {LABEL_FIELDS.map(({ lang, label }) => (
            <div key={lang} className="category-label-row">
              <label htmlFor={`cat-label-${lang}`}>{label}</label>
              <input
                id={`cat-label-${lang}`}
                value={form.labels[lang] || ""}
                maxLength={60}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, labels: { ...prev.labels, [lang]: e.target.value } }))
                }
              />
            </div>
          ))}
        </fieldset>

        {isNew && (
          <label className="category-checkbox">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            />
            Show in the app right away
          </label>
        )}

        <div className="modal-actions">
          <button type="submit" className="primary-action-btn" disabled={saving}>
            {saving ? "Saving…" : isNew ? "Add category" : "Save"}
          </button>
          <button type="button" className="ghost-btn" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

const CategoriesPage = ({ module }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Categories are per edition: everything on this page works on one edition.
  const [edition, setEdition] = useState(
    EDITIONS.includes(module.defaultEdition) ? module.defaultEdition : EDITIONS[0]
  );
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  // One change at a time, so a reorder can't race a toggle.
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | category

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/categories", { params: { edition } });
      setCategories(data.categories || []);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load categories"));
    } finally {
      setLoading(false);
    }
  }, [edition]);

  useEffect(() => {
    load();
  }, [load]);

  const replaceCategory = (updated) =>
    setCategories((prev) => prev.map((c) => (c._id === updated._id ? { ...c, ...updated } : c)));

  const run = async (action, successText) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      if (successText) setNotice(successText);
    } catch (err) {
      setError(errorMessage(err, "The change could not be saved"));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (category) =>
    run(async () => {
      const next = !category.is_active;
      // Optimistic, reverted if the save fails.
      replaceCategory({ ...category, is_active: next });
      try {
        const { data } = await api.patch(`/categories/${category._id}`, { is_active: next });
        replaceCategory(data.category);
      } catch (err) {
        replaceCategory(category);
        throw err;
      }
    }, `"${category.name}" is now ${category.is_active ? "hidden in" : "shown in"} the app for ${editionLabel(edition)}.`);

  const move = (index, delta) =>
    run(async () => {
      const target = index + delta;
      if (target < 0 || target >= categories.length) return;
      const previous = categories;
      const next = [...categories];
      [next[index], next[target]] = [next[target], next[index]];
      setCategories(next);
      try {
        const { data } = await api.put("/categories/order", {
          edition,
          ids: next.map((c) => c._id)
        });
        setCategories(data.categories || next);
      } catch (err) {
        setCategories(previous);
        if (err?.response?.status === 409) load();
        throw err;
      }
    }, "Order saved.");

  const remove = (category) => {
    if (!window.confirm(`Delete the category "${category.name}"? This can't be undone.`)) return;
    run(async () => {
      await api.delete(`/categories/${category._id}`);
      setCategories((prev) => prev.filter((c) => c._id !== category._id));
    }, `"${category.name}" was deleted.`);
  };

  const save = (form) =>
    run(async () => {
      if (editing === "new") {
        const { data } = await api.post("/categories", { ...form, edition });
        setCategories((prev) => [...prev, data.category]);
      } else {
        const { data } = await api.patch(`/categories/${editing._id}`, {
          name: form.name,
          labels: form.labels
        });
        replaceCategory(data.category);
      }
      setEditing(null);
    }, editing === "new" ? `"${form.name}" was added.` : "Category saved.");

  const activeCount = categories.filter((c) => c.is_active && c.isTab).length;

  return (
    <AdminLayout module={module} title="Categories">
      <div className="warning-banner">
        <strong>Live in the app, per edition.</strong> Each edition has its own categories. Turning
        one off hides its tab for readers of that edition on the app&apos;s next refresh (launch,
        return to the app, or pull to refresh); other editions are not affected. Its articles are
        kept and still appear in Latest News. The order here is that edition&apos;s tab order.
      </div>

      <div className="category-edition-picker" role="tablist" aria-label="Edition">
        {EDITIONS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={key === edition}
            className={`category-edition-tab ${key === edition ? "is-active" : ""}`.trim()}
            disabled={busy}
            onClick={() => {
              if (key === edition) return;
              setNotice("");
              setError("");
              setEdition(key);
            }}
          >
            {editionLabel(key)}
          </button>
        ))}
      </div>
      {!isAdmin && (
        <p className="warning-banner">You can view categories. Only admins can change them.</p>
      )}
      {error && <p className="error-banner">{error}</p>}
      {notice && <p className="success-banner">{notice}</p>}

      <div className="toolbar-row">
        <div className="toolbar-actions">
          {isAdmin && (
            <PrimaryActionButton label="Add category" onClick={() => setEditing("new")} />
          )}
        </div>
        {!loading && (
          <span className="category-summary">
            {editionLabel(edition)}: {activeCount} tab{activeCount === 1 ? "" : "s"} shown in the app ·{" "}
            {categories.length} categories
          </span>
        )}
      </div>

      {loading ? (
        <p className="loading-text">Loading categories…</p>
      ) : (
        <div className="table-card">
          <div className="table-wrap">
            <table className="category-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Category</th>
                  <th>Articles</th>
                  <th>Show in app</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty-cell">No categories yet.</td>
                  </tr>
                )}
                {categories.map((category, index) => (
                  <tr key={category._id} className={category.is_active ? "" : "category-row-off"}>
                    <td>
                      <div className="table-actions">
                        <span className="category-position">{index + 1}</span>
                        <button
                          type="button"
                          className="icon-btn"
                          title="Move up"
                          aria-label={`Move ${category.name} up`}
                          disabled={!isAdmin || busy || index === 0}
                          onClick={() => move(index, -1)}
                        >↑</button>
                        <button
                          type="button"
                          className="icon-btn"
                          title="Move down"
                          aria-label={`Move ${category.name} down`}
                          disabled={!isAdmin || busy || index === categories.length - 1}
                          onClick={() => move(index, 1)}
                        >↓</button>
                      </div>
                    </td>
                    <td>
                      <strong>{category.name}</strong>
                      <span className="category-slug">{category.slug}</span>
                      {!category.isTab && (
                        <span
                          className="category-badge"
                          title={
                            category.slug === "breaking_news"
                              ? "Shown as the breaking-news banner, not as a tab"
                              : category.slug === "latest_news"
                                ? "The app's first tab already shows all news"
                                : "Retired section, not shown as a tab"
                          }
                        >
                          {category.slug === "breaking_news" ? "Banner" : "Not a tab"}
                        </span>
                      )}
                    </td>
                    <td>{category.articleCount}</td>
                    <td>
                      <label className="category-switch" title={category.is_active ? "ON: visible in the app" : "OFF: hidden in the app"}>
                        <input
                          type="checkbox"
                          role="switch"
                          checked={category.is_active}
                          aria-checked={category.is_active}
                          aria-label={`Show ${category.name} in the app`}
                          disabled={!isAdmin || busy}
                          onChange={() => toggle(category)}
                        />
                        <span className="category-switch-track" aria-hidden="true" />
                        <span className="category-switch-text">{category.is_active ? "ON" : "OFF"}</span>
                      </label>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="ghost-btn"
                          disabled={!isAdmin || busy}
                          onClick={() => setEditing(category)}
                        >Edit</button>
                        <button
                          type="button"
                          className="delete-btn"
                          title={
                            category.isProtected
                              ? "The app depends on this category. Turn it off instead."
                              : category.articleCount > 0
                                ? "Has articles. Turn it off instead, or move its articles first."
                                : "Delete"
                          }
                          aria-label={`Delete ${category.name}`}
                          disabled={!isAdmin || busy || category.isProtected || category.articleCount > 0}
                          onClick={() => remove(category)}
                        >✖</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <CategoryForm
          isNew={editing === "new"}
          initial={
            editing === "new"
              ? EMPTY_FORM
              : { ...EMPTY_FORM, ...editing, labels: { ...EMPTY_LABELS, ...(editing.labels || {}) } }
          }
          saving={busy}
          serverError={error}
          editionName={editionLabel(edition)}
          onCancel={() => {
            setEditing(null);
            setError("");
          }}
          onSubmit={save}
        />
      )}
    </AdminLayout>
  );
};

export default CategoriesPage;
